// src/redux/favorites/favoriteSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { doc, setDoc, getDoc, deleteDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

export const addToFavorites = createAsyncThunk(
  'favorites/addToFavorites',
  async ({ uid, favorite }, { rejectWithValue }) => {
    try {
      // normalize to always include id (job.id)
      const favToSave = {
        ...favorite,
        id: favorite.id
      };

      const docRef = doc(db, 'users', uid, 'data', 'favorites');

      // read existing
      const docSnap = await getDoc(docRef);
      let favorites = [];
      if (docSnap.exists()) {
        favorites = docSnap.data().favorites || [];
      }

      // avoid duplicates - check by job.id
      const exists = favorites.some((f) => f.id === favToSave.id);
      if (!exists) {
        favorites.push(favToSave);
      }

      await setDoc(docRef, { favorites });

      // read back authoritative copy
      const updatedSnap = await getDoc(docRef);
      const updatedFavorites = updatedSnap.exists() ? updatedSnap.data().favorites || [] : [];

      return { uid, favorite: favToSave, favorites: updatedFavorites };
    } catch (error) {
      return rejectWithValue(error.message || error);
    }
  }
);

export const removeFromFavorites = createAsyncThunk(
  'favorites/removeFromFavorites',
  async ({ uid, favoriteId }, { rejectWithValue }) => {
    try {
      const docRef = doc(db, 'users', uid, 'data', 'favorites');
      const docSnap = await getDoc(docRef);

      let favorites = [];
      if (docSnap.exists()) {
        favorites = docSnap.data().favorites || [];
        // filter using job.id as canonical id field
        favorites = favorites.filter((fav) => fav.id !== favoriteId);
        await setDoc(docRef, { favorites });
      }

      // read back authoritative copy
      const updatedSnap = await getDoc(docRef);
      const updatedFavorites = updatedSnap.exists() ? updatedSnap.data().favorites || [] : [];

      return { uid, favoriteId, favorites: updatedFavorites };
    } catch (error) {
      return rejectWithValue(error.message || error);
    }
  }
);

export const fetchFavorites = createAsyncThunk(
  'favorites/fetchFavorites',
  async (uid, { rejectWithValue }) => {
    try {
      const docSnap = await getDoc(doc(db, 'users', uid, 'data', 'favorites'));
      if (docSnap.exists()) {
        return { uid, favorites: docSnap.data().favorites || [] };
      }
      return { uid, favorites: [] };
    } catch (error) {
      return rejectWithValue(error.message || error);
    }
  }
);

export const addSellerToFavorites = createAsyncThunk(
  'favorites/addSellerToFavorites',
  async ({ uid, seller }, { rejectWithValue }) => {
    try {
      const sellerId = seller.uid || seller.id;
      const docRef = doc(db, 'users', uid, 'favoriteSellers', sellerId);
      
      await setDoc(docRef, {
        ...seller,
        sellerUid: sellerId,
        savedAt: new Date().toISOString()
      });

      // reverse index so seller can find followers
      const followersRef = doc(db, 'users', sellerId, 'data', 'followers');
      try {
        await updateDoc(followersRef, { followers: arrayUnion(uid) });
      } catch {
        await setDoc(followersRef, { followers: [uid] });
      }

      return { sellerId, seller };
    } catch (error) {
      return rejectWithValue(error.message || error);
    }
  }
);

export const removeSellerFromFavorites = createAsyncThunk(
  'favorites/removeSellerFromFavorites',
  async ({ uid, sellerId }, { rejectWithValue }) => {
    try {
      const docRef = doc(db, 'users', uid, 'favoriteSellers', sellerId);
      await deleteDoc(docRef);

      const followersRef = doc(db, 'users', sellerId, 'data', 'followers');
      try { await updateDoc(followersRef, { followers: arrayRemove(uid) }); } catch {}

      return { sellerId };
    } catch (error) {
      return rejectWithValue(error.message || error);
    }
  }
);

export const fetchFavoriteSellers = createAsyncThunk(
  'favorites/fetchFavoriteSellers',
  async (uid, { rejectWithValue }) => {
    try {
      const { getDocs, collection } = await import('firebase/firestore');
      const collectionRef = collection(db, 'users', uid, 'favoriteSellers');
      const snapshot = await getDocs(collectionRef);
      
      const favoriteSellers = [];
      snapshot.forEach((doc) => {
        favoriteSellers.push({ id: doc.id, ...doc.data() });
      });

      return { favoriteSellers };
    } catch (error) {
      return rejectWithValue(error.message || error);
    }
  }
);

const initialState = { favorites: [], favoriteSellers: [] };

export const favoriteSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    resetFavorites() {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(addToFavorites.fulfilled, (state, action) => {
        // replace with authoritative array
        state.favorites = action.payload.favorites || state.favorites;
      })
      .addCase(removeFromFavorites.fulfilled, (state, action) => {
        state.favorites = action.payload.favorites || state.favorites;
      })
      .addCase(fetchFavorites.fulfilled, (state, action) => {
        state.favorites = action.payload.favorites || [];
      })
      .addCase(addSellerToFavorites.fulfilled, (state, action) => {
        const exists = state.favoriteSellers.some((s) => s.id === action.payload.sellerId);
        if (!exists) {
          state.favoriteSellers.push({ id: action.payload.sellerId, ...action.payload.seller });
        }
      })
      .addCase(removeSellerFromFavorites.fulfilled, (state, action) => {
        state.favoriteSellers = state.favoriteSellers.filter((s) => s.id !== action.payload.sellerId);
      })
      .addCase(fetchFavoriteSellers.fulfilled, (state, action) => {
        state.favoriteSellers = action.payload.favoriteSellers || [];
      });
      
  }
});

export const { resetFavorites } = favoriteSlice.actions;
export default favoriteSlice.reducer;